import { BookmarkViewButton } from 'src/components/PageBookmarks/BookmarkControls';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DNABox,
  DNAButton,
  DNAButtonProps,
  DNAConditionalWrapper,
  DNAContextMenu,
  DNAText,
  useOrientationChange,
} from '@alucio/lux-ui';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
import { FileType, MeetingType, Sentiment } from '@alucio/aws-beacon-amplify/src/models';
import { isDocumentVersionORM, isFolderItemORM } from 'src/types/typeguards';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';
import { useMeetingsState } from 'src/state/context/Meetings/MeetingsStateProvider';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider';
import { useHighlighter } from 'src/components/Highlighter/HighlighterProvider';
import useFullScreen from 'src/hooks/useFullScreen/useFullScreen';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import PresentationNav from 'src/screens/Meetings/SharedComponents/PresentationNav';
import OpenedPresentations from
  'src/screens/Meetings/PresentationControls/TabContent/OpenedPresentations/OpenedPresentations';
import { ActionBarState } from 'src/screens/Meetings/SharedComponents/PresentationMenu';
import { CanvasVariantEnum } from 'src/components/Highlighter/UseCanvas';
import { NOTES } from 'src/screens/Meetings/SharedComponents/Notes/AllNotes';
import { getPresentationVirtualStatus } from '../../util';
import * as logger from 'src/utils/logger';

const HTML_MULTI_PAGE_DEBUG_PREFIX = '[HTML_MP_DEBUG]';

type ButtonDefaultVariants = Record<DeviceMode, DNAButtonProps>
type AdditionalActionGroups = 'default' | 'tabletSentiments'

export const useInPersonActionBarSharedComponents = () => {
  const {
    meetingORM,
    endMeeting,
    setPresentationControlsVisible,
    slideRollVisible,
    toggleSlideRollVisibility,
    setSlideRollVisible,
    textSearchVisible,
    toggleTextSearchVisibility,
    canEndMeeting,
    openNotes,
    toggleMeetingNotes,
    toggleMeetingType,
    updateReaction,
    updateFollowUp,
    presentedMeta,
    actionBarState,
    setPresentationMenu,
  } = useMeetingsState();

  const featureFlags = useFeatureFlags(
    'enableMeetingNotes',
    'enableMeetingReactions',
    'enableMeetingFollowUp',
    'BEAC_7179_in_person_comments_and_sentiments',
  );

  const {
    highlighterVisible,
    setHighlighterMode,
    selectedType,
    setHighlighterVisible,
  } = useHighlighter();

  const { presentations, activePresentation, isPlayerReady, setPageNavigationView } = useContent();
  useEffect(() => {
    if (activePresentation?.pageNavigationView === 'bookmarks') setSlideRollVisible(true);
  }, [activePresentation?.presentable.id, activePresentation?.pageNavigationView, setSlideRollVisible]);
  const { orm } = { ...activePresentation?.presentable };
  const { isFullScreen, toggleFullScreen } = useFullScreen();
  const { deviceMode, platform } = useAppSettings()
  const isTablet = deviceMode === 'tablet';
  const orientation = useOrientationChange()
  const [showPresentations, setShowPresentations] = useState<boolean>(false);
  const [currentActionsGroup, setCurrentActionGroups] = useState<AdditionalActionGroups>('default')
  const isMeetingNoteOpen = openNotes.includes(NOTES.MEETING_NOTES)
  const presentationVirtualStatus = useMemo(() => getPresentationVirtualStatus(orm), [orm]);
  const isVirtual = meetingORM?.model.type === MeetingType.VIRTUAL;
  const showInPersonCommentsAndSentiments = featureFlags.BEAC_7179_in_person_comments_and_sentiments;

  useEffect(() => {
    logger.contentPresenting.highlighterPlayer.debug('Detect document changed, set highlighter visibility to false.')
    setHighlighterVisible(false)
  }, [activePresentation?.presentable.orm.model.id])

  const buttonDefaultVariants: ButtonDefaultVariants = {
    desktop: {
      appearance: 'ghost',
      status: 'subtle',
      colorMode: 'dark',
    },
    tablet: {
      appearance: 'ghost',
      status: 'subtle',
      colorMode: 'dark',
      size: orientation === 'LANDSCAPE' ? 'xl' : 'lg',
    },
  };

  const currentORM = isDocumentVersionORM(orm)
    ? orm
    : isFolderItemORM(orm)
      ? orm.relations.itemORM
      : undefined

  const isWebVideoHTMLDoc = isDocumentVersionORM(currentORM) && (
    currentORM.model.type === FileType.WEB ||
    currentORM.model.type === FileType.MP4 ||
    (currentORM.model.type === FileType.HTML && !currentORM.meta.isMultiPageHTML)
  );
  const isMultiPageHTMLDoc = isDocumentVersionORM(currentORM) &&
    currentORM.model.type === FileType.HTML &&
    !!currentORM.meta.isMultiPageHTML;

  const openSidePanel = () => {
    setSlideRollVisible(false);
    setPresentationControlsVisible(true);
  };

  const toggleShowPresentations = () => {
    setShowPresentations((showPresentations) => !showPresentations)
  };

  const buttonDefaults = buttonDefaultVariants[deviceMode]
  const presentationNav = !isWebVideoHTMLDoc && !!presentations.length && <PresentationNav />;

  logger.contentPresenting.contentProvider.debug(`${HTML_MULTI_PAGE_DEBUG_PREFIX} InPerson action bar nav visibility`, {
    documentType: isDocumentVersionORM(currentORM) ? currentORM.model.type : undefined,
    isMultiPageHTML: isDocumentVersionORM(currentORM) ? currentORM.meta.isMultiPageHTML : undefined,
    isWebVideoHTMLDoc,
    presentationsLength: presentations.length,
    hasActivePresentation: !!activePresentation,
    shouldShowPresentationNav: !isWebVideoHTMLDoc && !!presentations.length,
    presentationControlsVisible: actionBarState,
  });

  const myContentButton = (
    <DNAButton
      {...buttonDefaults}
      testID="my-content-button"
      onPress={openSidePanel}
    >
      My Content
    </DNAButton>
  );

  const presentationsButton = !!presentations.length && (
    <DNAPopover
      lazyMount
      placement="top"
      interactive={true}
      visible={showPresentations}
      onBackdropPress={toggleShowPresentations}
      type="menu"
      offset={30}
    >
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonDefaults}
          testID="files-button"
          status={showPresentations ? 'primary' : 'subtle'}
          onPress={toggleShowPresentations}
        >
          Opened Files
        </DNAButton>
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <OpenedPresentations
          variant="actionBar"
          onClose={toggleShowPresentations}
        />
      </DNAPopover.Content>
    </DNAPopover>
  );

  const slidesButton = !!activePresentation && !isWebVideoHTMLDoc && !!presentations.length && (
    <DNABox appearance="row" spacing="sm">
      <DNAButton
        {...buttonDefaults}
        testID="in-person-meeting-slides-btn"
        status={slideRollVisible && activePresentation.pageNavigationView !== 'bookmarks' ? 'primary' : 'subtle'}
        onPress={() => {
          setPageNavigationView('all');
          if (activePresentation.pageNavigationView === 'bookmarks') setSlideRollVisible(true);
          else toggleSlideRollVisibility();
        }}
      >
        Slides
      </DNAButton>
      <BookmarkViewButton
        buttonProps={buttonDefaults}
        isOpen={slideRollVisible}
        onOpen={() => setSlideRollVisible(true)}
      />
    </DNABox>
  );

  const highlighterButton = !!activePresentation && !isMultiPageHTMLDoc && (
    <DNAPopover
      placement="top"
      type="tooltip"
      offset={30}
      disablePopover={['tabletPWA']}
    >
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonDefaults}
          key="Marker"
          testID="toggle-marker-button"
          iconLeft="highlighter"
          onPress={() => setPresentationMenu(ActionBarState.HighlighterMenu)}
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">Highlighter</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  );

  const highlighterActions = !!activePresentation && !isMultiPageHTMLDoc && (
    <DNABox spacing="sm">
      <DNAButton
        {...buttonDefaults}
        testID="back-btn"
        iconLeft="arrow-left"
        onPress={() => {
          setPresentationMenu(ActionBarState.PresentationMenu)
          setHighlighterVisible(false)
        }}
      >
        Back
      </DNAButton>
      <DNAButton
        {...buttonDefaults}
        testID="arrow-btn"
        status={highlighterVisible && selectedType === CanvasVariantEnum.arrow
          ? 'primary'
          : buttonDefaults.status
        }
        iconLeft="arrow-up-right"
        onPress={() => setHighlighterMode(CanvasVariantEnum.arrow)}
      >
        Arrow
      </DNAButton>
      <DNAButton
        {...buttonDefaults}
        testID="box-btn"
        status={highlighterVisible && selectedType === CanvasVariantEnum.square
          ? 'primary'
          : buttonDefaults.status
        }
        iconLeft="square-fill"
        onPress={() => setHighlighterMode(CanvasVariantEnum.square)}
      >
        Box highlighter
      </DNAButton>
    </DNABox>
  );

  const highlighterContextMenu = !!activePresentation && !isMultiPageHTMLDoc && (
    <DNAConditionalWrapper
      condition={!selectedType}
      wrapper={children => (
        <DNAPopover
          placement="top"
          type="tooltip"
          offset={30}
          disablePopover={['tabletPWA']}
        >
          <DNAPopover.Anchor>
            <DNAContextMenu placement="top" style={{ bottom: 8 }}>
              <DNAContextMenu.Anchor>
                {children}
              </DNAContextMenu.Anchor>
              <DNAContextMenu.Items>
                <DNAContextMenu.Item
                  icon="arrow-up-right"
                  title="Arrow"
                  onPress={() => setHighlighterMode(CanvasVariantEnum.arrow)}
                  testID="arrow-btn"
                />
                <DNAContextMenu.Item
                  icon="square-fill"
                  title="Box highlighter"
                  onPress={() => setHighlighterMode(CanvasVariantEnum.square)}
                  testID="box-btn"
                />
              </DNAContextMenu.Items>
            </DNAContextMenu>
          </DNAPopover.Anchor>
          <DNAPopover.Content>
            <DNAText colorMode="dark">Highlighter</DNAText>
          </DNAPopover.Content>
        </DNAPopover>
      )}
    >
      <DNAButton
        {...buttonDefaults}
        key="Marker"
        testID="toggle-marker-button"
        status={selectedType ? 'primary' : 'subtle'}
        iconLeft="highlighter"
        onPress={() => {
          if (selectedType) setHighlighterVisible(false)
        }}
      />
    </DNAConditionalWrapper>
  );

  const textSearchButton = !!activePresentation && !isWebVideoHTMLDoc && !!presentations.length && (
    <DNAPopover
      placement="top"
      type="tooltip"
      offset={30}
      disablePopover={['tabletPWA']}
    >
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonDefaults}
          testID="in-person-meeting-search-in-document-btn"
          status={textSearchVisible ? 'primary' : 'subtle'}
          iconLeft="magnifying-glass"
          onPress={toggleTextSearchVisibility}
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">Search</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  );

  const reactionButtons = !!activePresentation && featureFlags.enableMeetingReactions ? (
    <DNABox spacing="sm">
      <DNAButton
        {...buttonDefaults}
        testID="positive-reaction-btn"
        iconLeft={presentedMeta?.sentiment === Sentiment.NEGATIVE ? 'caret-up-fill' : 'caret-up'}
        iconStyle={presentedMeta?.sentiment === Sentiment.POSITIVE ? { color: colors['color-success-500'] } : undefined}
        onPress={() => updateReaction(Sentiment.POSITIVE)}
      />
      <DNAButton
        {...buttonDefaults}
        testID="negative-reaction-btn"
        iconLeft={presentedMeta?.sentiment === Sentiment.NEGATIVE ? 'caret-down-fill' : 'caret-down'}
        onPress={() => updateReaction(Sentiment.NEGATIVE)}
      />
    </DNABox>
  ) : null;

  const followUpButton = !!activePresentation && featureFlags.enableMeetingFollowUp ? (
    <DNAPopover
      placement="top"
      type="tooltip"
      offset={30}
      disablePopover={['tabletPWA']}
    >
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonDefaults}
          testID="follow-up-flag-btn"
          iconLeft={presentedMeta?.followUp ? 'flag-fill' : 'flag'}
          onPress={updateFollowUp}
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">Flag for follow-up</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  ) : null;

  const sentimentsContextMenu = showInPersonCommentsAndSentiments && !!activePresentation &&
    (featureFlags.enableMeetingReactions || featureFlags.enableMeetingFollowUp) ? (
      <DNAPopover
        placement="top"
        type="tooltip"
        offset={30}
        disablePopover={['tabletPWA']}
      >
        <DNAPopover.Anchor>
          <DNAContextMenu placement="top" style={{ bottom: 8 }}>
            <DNAContextMenu.Anchor>
              <DNAButton
                {...buttonDefaults}
                status={(presentedMeta?.sentiment || presentedMeta?.followUp) ? 'primary' : 'subtle'}
                iconLeft="smiley"
              />
            </DNAContextMenu.Anchor>
            <DNAContextMenu.Items>
              {featureFlags.enableMeetingReactions &&
                <DNAContextMenu.Item
                  icon={presentedMeta?.sentiment === Sentiment.POSITIVE ? 'caret-up-fill' : 'caret-up'}
                  iconStatus={presentedMeta?.sentiment === Sentiment.POSITIVE ? 'success' : 'tertiary'}
                  title="Positive"
                  onPress={() => updateReaction(Sentiment.POSITIVE)}
                  testID="positive-reaction-btn"
                />
              }
              {featureFlags.enableMeetingReactions &&
                <DNAContextMenu.Item
                  icon={presentedMeta?.sentiment === Sentiment.NEGATIVE ? 'caret-down-fill' : 'caret-down'}
                  iconStatus={presentedMeta?.sentiment === Sentiment.NEGATIVE ? 'warning' : 'tertiary'}
                  title="Negative"
                  onPress={() => updateReaction(Sentiment.NEGATIVE)}
                  testID="negative-reaction-btn"
                />
              }
              {featureFlags.enableMeetingFollowUp &&
                <DNAContextMenu.Item
                  icon={presentedMeta?.followUp ? 'flag-fill' : 'flag'}
                  iconStatus={presentedMeta?.followUp ? 'primary' : 'tertiary'}
                  title="Flag for follow-up"
                  onPress={updateFollowUp}
                  testID="follow-up-flag-btn"
                />
              }
            </DNAContextMenu.Items>
          </DNAContextMenu>
        </DNAPopover.Anchor>
        <DNAPopover.Content>
          <DNAText colorMode="dark">Sentiments</DNAText>
        </DNAPopover.Content>
      </DNAPopover>
    ) : null;

  const myNotesButton =
    showInPersonCommentsAndSentiments &&
    !!activePresentation &&
    !!presentations.length &&
    featureFlags.enableMeetingNotes ? (
      <DNAPopover
        placement="top"
        type="tooltip"
        offset={30}
        disablePopover={['tabletPWA']}
      >
        <DNAPopover.Anchor>
          <DNAButton
            {...buttonDefaults}
            key="Comments"
            testID="toggle-comments-button"
            status={isMeetingNoteOpen ? 'primary' : 'subtle'}
            iconLeft="note-pencil"
            onPress={toggleMeetingNotes}
          />
        </DNAPopover.Anchor>
        <DNAPopover.Content>
          <DNAText colorMode="dark">Comments</DNAText>
        </DNAPopover.Content>
      </DNAPopover>
      ) : null;

  const fullScreenButton = (deviceMode === 'desktop' || platform.includes('desktop')) ? (
    <DNAPopover
      placement="top"
      type="tooltip"
      offset={30}
      disablePopover={['tabletPWA']}
    >
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonDefaults}
          testID="in-person-meeting-full-screen-btn"
          iconRight={isFullScreen ? 'arrows-in-simple' : 'arrows-out-simple'}
          onPress={toggleFullScreen}
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">Full screen</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  ) : null;

  const endMeetingButton = (
    <DNAButton
      {...buttonDefaults}
      testID="in-person-meeting-end-meeting-btn"
      status="danger"
      iconLeft={isTablet ? 'x' : undefined}
      onPress={endMeeting}
      disabled={!canEndMeeting}
      children={isTablet ? undefined : 'End meeting'}
    />
  );

  const changeToVirtualModeTooltipMessage =
    !isPlayerReady
      ? `${isVirtual ? 'Exit' : ''} Presenter View ${!isVirtual ? 'available' : ''} once document is loaded`
      : (presentationVirtualStatus.isHTMLWebPresentation && presentationVirtualStatus.message) ||
      'Use Presenter View';
  const changeToVirtualModeButton = platform.includes('desktop') && (
    <DNAPopover
      placement="top"
      type="tooltip"
      offset={30}
    >
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonDefaults}
          testID="change-meeting-mode-button"
          iconRight={isVirtual ? 'lectern' : 'presentation-chart'}
          onPress={toggleMeetingType}
          disabled={presentationVirtualStatus.isHTMLWebPresentation || !isPlayerReady}
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">{changeToVirtualModeTooltipMessage}</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  );

  const collapseButton = (
    <DNAButton
      {...buttonDefaults}
      testID="direct-share-mode-collapse-btn"
      iconRight={
        actionBarState === ActionBarState.PresentationMenu
          ? 'arrow-line-right'
          : 'arrow-line-left'
      }
      onPress={() => setPresentationMenu(
        actionBarState === ActionBarState.PresentationMenu ? ActionBarState.Collapsed : ActionBarState.PresentationMenu,
      )}
    />
  );

  const CurrentActionsToggle = useCallback<React.FC<{
    group: AdditionalActionGroups,
    isIconButton?: boolean
  } & DNAButtonProps>>(
    (props) => {
      const { group, isIconButton, ...buttonProps } = props
      return (
        <DNAButton
          {...buttonDefaults}
          {...buttonProps}
          onPress={() => setCurrentActionGroups(group)}
        />
      )
    },
    [setCurrentActionGroups],
  );

  return {
    presentationNav,
    myContentButton,
    presentationsButton,
    slidesButton,
    highlighterButton,
    highlighterActions,
    highlighterContextMenu,
    textSearchButton,
    reactionButtons,
    followUpButton,
    sentimentsContextMenu,
    myNotesButton,
    fullScreenButton,
    endMeetingButton,
    changeToVirtualModeButton,
    collapseButton,
    actionBarState,
    CurrentActionsToggle,
    currentActionsGroup,
  }
}

export default useInPersonActionBarSharedComponents
